import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LifeBuoy, CheckCircle2 } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const ISSUE_TYPES = ["Order Status Update", "RMA Request", "Warranty Claim"];

export default function SupportForm({ open, onOpenChange, onSubmit, defaults }) {
  const { t } = useLocale();
  const [form, setForm] = useState({ order_id: "", infoshop_pn: "", issue_type: "", description: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ order_id: defaults?.order_id || "", infoshop_pn: defaults?.infoshop_pn || "", issue_type: "", description: "" });
      setErrors({});
      setResult(null);
    }
  }, [open, defaults]);

  const submit = async () => {
    const next = {};
    if (!form.order_id.trim() && !form.infoshop_pn.trim())
      next.order_id = "Provide Order ID or UniBuy™ Part Number";
    if (!form.issue_type) next.issue_type = "Select issue type";
    if (!form.description.trim() || form.description.trim().length < 5)
      next.description = "Describe the issue (5+ chars)";
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      const res = await onSubmit(form);
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setResult(null); onOpenChange(o); }}>
      <DialogContent data-testid="support-dialog" className="max-w-lg">
        {!result ? (
          <>
            <DialogHeader>
              <div className="h-11 w-11 rounded-md bg-[#0072CE]/10 border border-[#0072CE]/15 text-[#0072CE] flex items-center justify-center mb-3">
                <LifeBuoy size={20} />
              </div>
              <DialogTitle className="font-display tracking-tight text-2xl">{t("support.title")}</DialogTitle>
              <DialogDescription>{t("support.subtitle")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold">{t("confirm.order_id")}</Label>
                  <Input value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} data-testid="support-order-id" className="mt-1.5" placeholder="OMS-…" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">{t("card.infoshop_pn")}</Label>
                  <Input value={form.infoshop_pn} onChange={(e) => setForm({ ...form, infoshop_pn: e.target.value })} data-testid="support-pn" className="mt-1.5" placeholder="IS-…" />
                </div>
              </div>
              {errors.order_id && <p className="text-xs text-rose-600">{errors.order_id}</p>}

              <div>
                <Label className="text-sm font-semibold">{t("support.subject")} <span className="text-rose-600">*</span></Label>
                <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="support-issue-type"><SelectValue placeholder={t("support.subject")} /></SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map((iss) => <SelectItem key={iss} value={iss}>{iss}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.issue_type && <p className="text-xs text-rose-600 mt-1">{errors.issue_type}</p>}
              </div>

              <div>
                <Label className="text-sm font-semibold">{t("support.description")} <span className="text-rose-600">*</span></Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="support-description"
                  rows={4}
                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0072CE]"
                  placeholder={t("support.description")}
                />
                {errors.description && <p className="text-xs text-rose-600 mt-1">{errors.description}</p>}
              </div>
            </div>

            <Button onClick={submit} disabled={submitting} data-testid="support-submit"
              className="mt-2 w-full bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md py-5 font-semibold">
              {submitting ? "…" : t("support.submit")}
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="h-11 w-11 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center mb-3">
                <CheckCircle2 size={20} />
              </div>
              <DialogTitle className="font-display tracking-tight text-2xl">Ticket created</DialogTitle>
              <DialogDescription>Reference: <span className="font-mono text-slate-900 font-semibold">{result.ticket_id}</span></DialogDescription>
            </DialogHeader>
            <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">What happens next</div>
              <ul className="space-y-2 text-sm text-slate-700">
                {result.documented_actions?.map((a, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#0072CE] shrink-0" />{a}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
